/**
 * Form Component
 *
 * @see pages/step-form.php
 */
"use strict";

import axios from "axios";
import Qs from "qs";
import Pristine from "pristinejs/dist/pristine.min.js";
import { UTM } from "../utm.js";
import { Mutiny } from "../mutiny.js";
import { TrustedForm } from "../trusted-form.js";

export class AmericorForm {
  
  /**
   * Wrapper
   * 
   * @var object
   */
  static wrapper = document.querySelector('.credit9-offer-form');

  /**
   * Default Channel, Company, Redirect URL
   * 
   * Add data attributes to the form wrapper to define these values
   * 
   * @var string
   */
  static channel      = AmericorForm.wrapper.dataset.channel ?? undefined;
  static company      = AmericorForm.wrapper.dataset.company ?? undefined;
  static redirect_url = AmericorForm.wrapper.dataset.redirectUrl ?? undefined;

  /**
   * Default Vendor ID
   *
   * @var int
   */
  static vid;

  /**
   * Define form field querySelectors
   *
   * Key should match Lead API field name return to properly validate fields
   *
   * @see https://admin.americor.com/swagger/doc
   * @var object
   */
  static form_fields = {
    offer: "input[name=reservation_code]",
    debt: "select[name=debt]",
    firstName: "input[name=firstName]",
    lastName: "input[name=lastName]",
    phone: "input[name=phone]",
    email: "input[name=email]",
    street: "input[name=street]",
    city: "input[name=city]",
    zip: "input[name=zip]",
  };

  /**
   * Default Pristine form validation config
   *
   * Change this to match form markup
   *
   * @see https://pristine.js.org/
   * @var object
   */
  static form_validation_config = {
    classTo: "form-group",
    errorClass: "has-error",
    successClass: "has-success",
    errorTextParent: "form-group",
    errorTextTag: "div",
    errorTextClass: "help-block-error",
  };

  /**
   * Lead UUID
   *
   * @var string
   */
  static uuid = localStorage.getItem("lead_uuid") ?? null;

  /**
   * UTM Helper
   *
   * @var UTM
   */
  static utm = new UTM();

  /**
   * Mutiny object
   *
   * @var Mutiny
   */
  static mutiny = new Mutiny();

  /**
   * Trusted Form Helper
   *
   * @var TrustedForm
   */
  static trusted_form = new TrustedForm();

  /**
   * Constructor
   *
   * @returns
   */
  constructor() {
    if ( AmericorForm.wrapper == null ) return;

    window.dataLayer = window.dataLayer || [];

    this.get_zip = true;

    this.toggle_hash_view();
    window.addEventListener("hashchange", this.toggle_hash_view);

    this.init_offer_code_step();
    this.init_contact_info_step();
    this.init_address_step();
    this.get_parameters();
  }

  /**
   * Get Vendor ID from URL
   *
   * @returns int
   */
  get_vendor_id() {
    const urlParams = new URLSearchParams(window.location.search);

    AmericorForm.vid = ( urlParams.has("vid") ) ? urlParams.get("vid") : AmericorForm.wrapper.dataset.vendorId;

    return AmericorForm.vid;
  }

  /**
   * Get parameters with UTM and Mutiny values
   *
   * @returns object
   */
  get_parameters() {
    return {
      ...AmericorForm.utm.get_parameters(),
      ...AmericorForm.mutiny.get_parameters(),
    };
  }

  /**
   * Toggle step based on URL hash
   * 
   */
  toggle_hash_view()
  {
    let hash = window.location.hash;
        hash = hash.replace("#wizard-", "");

    if ( document.getElementById(hash) ) {
      document.querySelectorAll('.step:not(#' + hash + ')').forEach( step => step.classList.remove('active') );
      document.getElementById(hash).classList.add('active');
    }
    else
    {
      document.querySelector('.step.default').classList.add('active');
      
      window.location.hash = 'wizard-' + document.querySelector('.step.default').id;
    }
  }

  /**
   * Init Offer Code Step - Step #0
   */
  init_offer_code_step() {
    const _self     = this;
    const form      = document.querySelector("form#form-w0");
    const next_step = form.getAttribute("data-next-step");
    const pristine  = new Pristine(form, AmericorForm.form_validation_config);

    form.addEventListener("submit", function (e) {
      e.preventDefault();

      if ( ! pristine.validate() ) return;

      const form_data = {
        vendorId: _self.get_vendor_id(),
        offer_code: document.querySelector(AmericorForm.form_fields.offer).value,
      };

      // Submit form data with callback
      _self.submit( form, pristine, form_data, "offer_code", ( response ) => {
        document.querySelector(AmericorForm.form_fields.firstName).value = response.firstName;
        document.querySelector(AmericorForm.form_fields.lastName).value  = response.lastName;
        document.querySelector(AmericorForm.form_fields.street).value    = response.street;
        document.querySelector(AmericorForm.form_fields.city).value      = response.city;
        document.querySelector(AmericorForm.form_fields.zip).value       = response.zip;

        _self.get_zip = false;

        window.dataLayer.push({
          event: "mailer_code",
          lead_id: AmericorForm.uuid,
        });

        window.location.href = "#wizard-" + next_step;
      });
    });
  }

  /**
   * Init Contacts Info Step - Step #2
   *
   */
  init_contact_info_step() {
    const _self     = this;
    const form      = document.querySelector("form#form-w4");
    const next_step = form.getAttribute("data-next-step");
    const pristine  = new Pristine(form, AmericorForm.form_validation_config);

    form.addEventListener("submit", function (e) {
      e.preventDefault();

      // Validate form
      if ( ! pristine.validate() ) return;

      let form_data = {
        firstName: document.querySelector(AmericorForm.form_fields.firstName).value,
        lastName: document.querySelector(AmericorForm.form_fields.lastName).value,
        phone: document.querySelector(AmericorForm.form_fields.phone).value,
        email: document.querySelector(AmericorForm.form_fields.email).value,
        zip: document.querySelector(AmericorForm.form_fields.zip).value,
        isTcpaConfirmed: true,
        get_zip: _self.get_zip,
      };

      _self.submit( form, pristine, form_data, "update_lead", (response) => {
        if (response.city) {
          document.querySelector(AmericorForm.form_fields.city).value = response.city;
        }

        if (response.zip) {
          document.querySelectorAll(AmericorForm.form_fields.zip).forEach(( element ) => ( element.value = response.zip ));
        }

        window.dataLayer.push({
          event: "leadInfo",
          lead_id: AmericorForm.uuid,
        });

        window.location.href = "#wizard-" + next_step;
      });
    });
  }

  /**
   * Init Contacts Info Step - Step #3
   *
   */
  init_address_step() {
    const _self        = this;
    const form         = document.querySelector("form#form-w11");
    const pristine     = new Pristine(form, AmericorForm.form_validation_config);
    
    let redirect_url = AmericorForm.redirect_url ?? '/thank-you/';

    form.addEventListener("submit", function (e) {
      e.preventDefault();

      // Validate form
      if ( ! pristine.validate() ) return;

      let form_data = {
        street: document.querySelector(AmericorForm.form_fields.street).value,
        city: document.querySelector(AmericorForm.form_fields.city).value,
        zip: document.querySelector(AmericorForm.form_fields.zip).value,
        isCompleted: true,
      };

      _self.submit( form, pristine, form_data, "update_lead", ( response ) => {
        if (response.callSchedulerLink) {
          redirect_url = response.callSchedulerLink;
        }

        let data = {
          event: "leadGenerated",
          lead_id: response.uuid,
          firstName: response.firstName,
          lastName: response.lastName,
          phoneNumber: response.phone,
          email: response.email,
          city: response.city,
          zip: response.zip,
          debt_amount: response.debt,
          credit_pull: response.debt_credit_report,
        };

        window.dataLayer.push(data);

        window.location.href = redirect_url;
      });
    });
  }

  /**
   * Submit form data to API
   *
   * @param object form
   * @param object pristine - Pristine instance to trigger callback error validation
   * @param object form_data
   * @param string action
   * @param function callback
   * @return Promise
   */
  async submit(form, pristine, form_data, action, callback) {
    const _self = this;

    form.classList.add("loading");

    form_data.params = this.get_parameters();

    // Add subId params to form_data
    if ( Object.keys(form_data.params).length )
    {
      Object.keys(form_data.params).filter( k => {
        if ( k.startsWith('subId') )
        {
          form_data[k] = form_data.params[k];

          delete form_data.params[k];
        }
      });
    }

    // Add UUID if updating a lead
    if ( action == 'update_lead' )
    {
      form_data.uuid = AmericorForm.uuid;
    }

    // Add trusted form token, custom channel and company if creating a lead
    if ( action == 'create_lead' )
    {
      form_data.trusted_form_token = await AmericorForm.trusted_form.get_token();

      if ( AmericorForm.channel !== undefined )
      {
        form_data.channel = AmericorForm.channel;
      }

      if ( AmericorForm.company !== undefined )
      {
        form_data.company = AmericorForm.company;
      }
    }

    console.table(form_data);

    let submit = this.make_api_request(form_data, action);

    submit.then((response) => {
      form.classList.remove("loading");

      console.log(response);

      // Validate response
      if ( !response || !_self.validate_response(response, pristine) ) return;

      // Set Lead UUID
      if ( response.uuid )
      {
        AmericorForm.uuid = response.uuid;

        localStorage.setItem("lead_uuid", response.uuid);
      }

      callback(response);
    });
  }

  /**
   * Make request to API
   *
   * @param object data
   * @param string action
   * @return Promise
   */
  async make_api_request(data, action) {
    let params = data.params || {};

    try {
      const response = await axios.post( americor.ajaxurl, Qs.stringify(
        {
          action: action,
          content: data,
          params: params
        }),
      );

      return response["data"];
    } catch (error) {
      console.log(error);
    }
  }

  /**
   *
   * Validate API response and display error messages
   *
   * @param object response
   * @param object pristine - Pristine instance to trigger callback error validation
   * @returns boolean
   */
  validate_response(response, pristine) {
    if ("status" in response && response.status == 400) {
      if (response.errors) {
        for (const key in response.errors) {
          const element = document.querySelector(AmericorForm.form_fields[key]);

          pristine.addError(element, response.errors[key]);
        }
      }

      return false;
    }

    return true;
  }
}

const step_form = new AmericorForm();